import React from 'react';
import { Alert, Box, Stack, Table, Typography } from '@mui/joy';
import { groupReviewRowsByItem } from '../utils/surveyDeltaReview';

export type FinalSurveyReviewTableSection = {
  id: string;
  title: string;
  rows: Array<{
    id: string;
    item?: string | null;
    label: string;
    original: string | null;
    final: string | null;
  }>;
};

const EMPTY_VALUE = '—';

function ReviewValueCell(props: {
  value: string | null;
  tone: 'original' | 'final';
}) {
  const hasValue = Boolean(props.value && String(props.value).trim());
  const isOriginal = props.tone === 'original';
  return (
    <td
      style={{
        backgroundColor: hasValue
          ? (isOriginal ? 'rgba(185, 28, 28, 0.10)' : 'rgba(22, 101, 52, 0.10)')
          : undefined,
        color: hasValue
          ? (isOriginal ? '#9b1c1c' : '#166534')
          : undefined,
        fontWeight: hasValue ? 600 : 400,
        verticalAlign: 'top',
        width: '32%',
      }}
    >
      {hasValue ? props.value : EMPTY_VALUE}
    </td>
  );
}

export function FinalSurveyReviewTable(props: {
  sections: FinalSurveyReviewTableSection[];
  emptyText?: string;
  originalHeading?: string;
}) {
  const originalHeading = props.originalHeading || 'Original';
  if (!props.sections.length) {
    return (
      <Alert color="success" variant="soft" sx={{ fontWeight: 700 }}>
        {props.emptyText || 'No differences were found between the original quote and the Final Survey.'}
      </Alert>
    );
  }

  return (
    <Stack gap={1.75}>
      {props.sections.map((section) => (
        <Box key={section.id}>
          <Typography level="title-sm" sx={{ mb: 0.75, fontWeight: 800 }}>
            {section.title}
          </Typography>
          <Table
            borderAxis="both"
            size="sm"
            sx={{
              tableLayout: 'fixed',
              '& th': { fontWeight: 800, bgcolor: 'background.level1' },
              '& td, & th': { verticalAlign: 'top' },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: '36%' }}>Detail</th>
                <th style={{ width: '32%' }}>{originalHeading}</th>
                <th style={{ width: '32%' }}>Final</th>
              </tr>
            </thead>
            <tbody>
              {groupReviewRowsByItem(section.rows).map((group, groupIndex) => (
                <React.Fragment key={`${section.id}:${group.item || 'general'}:${groupIndex}`}>
                  {group.item ? (
                    <tr>
                      <td
                        colSpan={3}
                        style={{
                          backgroundColor: 'var(--joy-palette-neutral-softBg)',
                          fontWeight: 800,
                        }}
                      >
                        {group.item}
                      </td>
                    </tr>
                  ) : null}
                  {group.rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.label}</td>
                      <ReviewValueCell value={row.original} tone="original" />
                      <ReviewValueCell value={row.final} tone="final" />
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </Box>
      ))}
    </Stack>
  );
}
